import { getLocalStorageItem, setLocalStorageItem } from 'utils/storageUtils';

import { MessageTemplatesType, MessageTemplatesVariant } from './constants';

const STORAGE_PREFIX = 'recentlyUsedMessageTemplates';
const MAX_RECENT_TEMPLATES = 3;

// Mirrors the backend's `MessageTemplate::NON_STAGE_KEYS_TEMPLATE_TYPES`
// (apps/hire/app/models/message_template.rb). These template types are
// always included in the API response regardless of the current applicant's
// stage, so snapshots of these types are safe to surface cross-applicant
// without an eligibility check.
//
// Keep in sync with the backend constant.
const NON_STAGE_TEMPLATE_TYPES = new Set([
  'hired',
  'automated',
  'general',
  'email_pdf',
  'trigger',
  'i9_form',
  'multi_stage',
  'archived',
  'rejection',
  'tech_check',
  'video_recording',
  'w4_federal_form',
  'w4_state_form',
]);

/**
 * Build the local storage key for a given dropdown context. Recents are a
 * single shared list across applicants — the recruiter messages applicant A,
 * navigates to applicant B, and expects the previously-used template to
 * surface. Per-template eligibility for the current applicant is enforced on
 * read via `isSnapshotEligible`.
 *
 * @param {object} context
 * @param {string} context.userId state.whoami.external_id
 * @param {string} context.variant MessageTemplatesVariant.EMAIL | .SMS | .WHATSAPP
 * @param {string} [context.templateType] one of MessageTemplatesType (defaults to GENERAL, matching the fetch)
 * @param {string} [context.whatsAppProvider] provider slug, only meaningful when variant === WHATSAPP
 *
 * @returns {string}
 */
const getTemplateKey = ({
  userId,
  variant,
  templateType,
  whatsAppProvider,
}) => {
  const effectiveType = templateType || MessageTemplatesType.GENERAL;
  const providerSuffix =
    variant === MessageTemplatesVariant.WHATSAPP && whatsAppProvider
      ? `::${whatsAppProvider}`
      : '';
  return `${STORAGE_PREFIX}::${variant}::${effectiveType}${providerSuffix}::${userId}`;
};

/**
 * Decide whether a stored template is eligible to surface for the current
 * dropdown context — i.e. whether the backend would return it right now.
 *
 * Backend behavior (ApiSelfServe::V2::MessageTemplatesController#type):
 *   - Non-stage template types are ALWAYS returned regardless of applicant.
 *   - When templateType === RECOLLECTION, only recollection templates are
 *     returned and the applicant's stage type is NOT added.
 *   - For every other templateType, the applicant's current stage template
 *     type is appended, so a stage-scoped template may or may not be
 *     eligible depending on the current applicant's stage.
 *
 * For the stage-scoped case we use the templates the backend just returned
 * (`currentFetchedIds`) as the eligibility signal — if the template is in
 * that set, the backend has confirmed it applies to the current applicant.
 */
const isSnapshotEligible = (template, context) => {
  // Recollection dropdown: backend doesn't filter by applicant stage type,
  // so any snapshot in this bucket applies to any applicant.
  if (context.templateType === MessageTemplatesType.RECOLLECTION) return true;

  // Non-stage-scoped template types are always returned by the backend.
  if (template.type && NON_STAGE_TEMPLATE_TYPES.has(template.type)) return true;

  // Stage-scoped: only eligible if the backend just confirmed it in the
  // current fetch. If we don't have that context yet (initial render before
  // the fetch resolves), don't surface stage-scoped templates.
  return Boolean(
    context.currentFetchedIds && context.currentFetchedIds.has(template.id),
  );
};

const isValidSnapshot = entry =>
  entry && typeof entry === 'object' && entry.id != null;

/**
 * Get recently used templates for the given dropdown context.
 *
 * Stored entries are full template snapshots (so pagination doesn't cause
 * page-2 templates to vanish) and are filtered per-request against the
 * eligibility signal derived from what the backend just returned.
 *
 * Silently ignores legacy entries from prior versions (id-only arrays).
 *
 * @param {object} context — see getTemplateKey / isSnapshotEligible
 *
 * @returns {array} recently used template objects
 */
export const getRecentlyUsedTemplates = context => {
  const templateKey = getTemplateKey(context);
  const stored = JSON.parse(getLocalStorageItem(templateKey)) || [];
  return stored
    .filter(isValidSnapshot)
    .filter(template => isSnapshotEligible(template, context));
};

/**
 * Record a template as recently used for the given dropdown context.
 *
 * Simple LRU eviction: newest first, capped at MAX_RECENT_TEMPLATES.
 *
 * @param {object} template
 * @param {object} context — see getTemplateKey
 *
 * @returns {void}
 */
export const updateRecentlyUsedTemplates = (template, context) => {
  if (!template || template.id == null) return;

  const templateKey = getTemplateKey(context);
  const stored = JSON.parse(getLocalStorageItem(templateKey)) || [];
  const existing = stored.filter(isValidSnapshot);
  const withoutDuplicate = existing.filter(({ id }) => id !== template.id);
  const updated = [template, ...withoutDuplicate].slice(
    0,
    MAX_RECENT_TEMPLATES,
  );

  setLocalStorageItem(templateKey, JSON.stringify(updated));
};

/**
 * Sort templates alphabetically by name
 *
 * @param {object} a template
 * @param {object} b template
 *
 * @returns {number}
 */
export const sortTemplatesAlphabetically = (a, b) => {
  if (a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase()) {
    return -1;
  }

  if (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) {
    return 1;
  }

  return 0;
};
